import './scene.css';
import './styles.css';
import './overlays.css';
import { ACCESSORIES, FACILITIES, GOODS, SUPPLY_ROUTES } from './data.js';
import { MEDIA } from './assets.js';
import { settlementJourney } from './journey.js';
import { runWorkShift, runTradingDay } from './flow.js';
import {
  createInitialState,
  configureStarterShelf,
  configureExpandedShelf,
  configureIntroShelf,
  startPlotProject,
  advanceToNextOpportunity,
  acceptMeetingOffer,
  assignEmployee,
  auditEmployee,
  counterMeetingOffer,
  declineMeetingOffer,
  getDerived,
  hireEmployee,
  moveAccessory,
  moveDockTreatment,
  orderFromRoute,
  placeAccessory,
  placeDockTreatment,
  purchasePlot,
  purchaseDockTreatment,
  repairProperty,
  resolveInteriorOpportunity,
  setBuyCeiling,
  setDisplay,
  setMeetingPolicy,
  setPrice,
  setRestockPolicy,
  startConstruction,
  tick,
  fireEmployee,
  beginPokerSession,
  checkpointPokerSession,
  endPokerSession,
  freezePokerSession,
  resumePokerSession,
} from './simulation.js';
import {
  clearSavedGame,
  deserializeState,
  loadGame,
  saveGame,
  serializeState,
} from './storage.js';
import { RiverAudio } from './audio.js';
import { formatCurrencyText } from './currency.js';
import {
  helpModalHtml,
  money,
  renderArrivals,
  renderHeader,
  renderLedger,
  renderWorkControls,
  updateOrderQuotes,
  SECTIONS,
  renderScene,
  shellHtml,
  startScreenHtml,
} from './ui.js';

const appRoot = document.querySelector('#app');
const audio = new RiverAudio();
const INTERIOR_FACILITIES = new Set(['shop-shack', 'shop-general', 'shop-emporium', 'shop-trading-house', 'livery', 'pottery-kiln', 'art-gallery', 'shipwright', 'repair-workshop', 'river-casino']);
const CASINO_PROTOCOL = 'incoprea:casino:v1';
const CASINO_SAFE_PHASES = new Set(['IDLE', 'SHOWDOWN', 'GAME_OVER']);

class TradingPostGame {
  constructor(root) {
    this.root = root;
    this.state = null;
    this.hasStoredGame = false;
    this.running = false;
    this.paused = true;
    this.speed = 1;
    this.muted = false;
    this.soundtrack = 'off';
    this.workspaceOpen = false;
    this.arrivalsOpen = false;
    this.activeTab = 'desk';
    this.selectedPlotIndex = 3;
    this.shiftResult = null;
    this.marketFilter = 'shelf';
    this.sectionTabs = Object.fromEntries(SECTIONS.map(section => [section.id, section.tabs[0]]));
    this.ledgerViews = new Map();
    this.sceneMode = 'river';
    this.interiorPlotIndex = null;
    this.selection = null;
    this.lastFrame = 0;
    this.lastRender = 0;
    this.minuteAccumulator = 0;
    this.lastSaveAtMinute = 0;
    this.lastLedgerLength = 0;
    this.knownSteamers = new Set();
    this.toastTimers = new Set();
    this.sceneAccumulator = 0;
    this.casino = null;
    this.wasPausedBeforeCasino = false;
  }

  boot() {
    this.root.innerHTML = shellHtml();
    try {
      this.state = loadGame();
      this.hasStoredGame = Boolean(this.state);
    } catch (error) {
      this.state = null;
      this.toast(`Stored save could not be read: ${error.message}`, 'bad');
    }
    if (!this.state) this.state = createInitialState(`trading-post-${new Date().toISOString().slice(0, 10)}`);
    this.lastLedgerLength = this.state.ledger.length;
    this.bind();
    this.render(true);
    this.root.insertAdjacentHTML('beforeend', startScreenHtml(this.hasStoredGame, 0, false));
    this.preloadAssets();
    requestAnimationFrame(time => this.frame(time));
  }

  async preloadAssets() {
    const core = [...new Set(['./media/v2/riverfront-diorama.png', MEDIA.facilities, MEDIA.vessels, MEDIA.road])];
    let complete = 0;
    let deferred = false;
    const total = core.length + 1;
    const update = () => {
      const percent = Math.round(complete / total * 100);
      const screen = this.root.querySelector('.start-screen');
      screen?.querySelector('[role="progressbar"]')?.setAttribute('aria-valuenow', String(percent));
      screen?.querySelector('[data-load-fill]')?.style.setProperty('--load-progress', `${percent}%`);
      const value = screen?.querySelector('[data-load-value]');
      if (value) value.textContent = `${percent}%`;
    };
    const loadImage = url => new Promise(resolve => {
      const image = new Image();
      image.decoding = 'async';
      let settled = false;
      const settle = failed => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        if (failed) deferred = true;
        resolve();
      };
      const timeout = setTimeout(() => settle(true), 5000);
      image.addEventListener('load', () => settle(false), { once: true });
      image.addEventListener('error', () => settle(true), { once: true });
      image.src = url;
    });
    const tracked = promise => promise.finally(() => { complete += 1; update(); });
    const font = document.fonts.load('24px "Ember Ledger Display"').catch(() => { deferred = true; });
    await Promise.all([...core.map(url => tracked(loadImage(url))), tracked(Promise.race([font, new Promise(resolve => setTimeout(resolve, 5000))]))]);
    const screen = this.root.querySelector('.start-screen');
    const status = screen?.querySelector('[data-load-label]');
    if (status) status.textContent = deferred ? 'Post ready · some artwork may still be loading' : 'River ready · choose your journey';
    screen?.classList.add('ready');
    screen?.querySelectorAll('[data-action="continue"],[data-action="new-game"]').forEach(button => { button.disabled = false; });
    // Rooms and later-game artwork never hold the start screen hostage.
    const remaining = [...new Set(Object.values(MEDIA))].filter(url => !core.includes(url));
    for (let index = 0; index < remaining.length; index += 6) await Promise.all(remaining.slice(index, index + 6).map(loadImage));
  }

  bind() {
    this.root.addEventListener('click', event => this.onClick(event));
    this.root.addEventListener('focusin', event => {
      if (event.target.matches('[data-sell-price],[data-buy-ceiling],[data-restock-target],[data-restock-reserve],[data-counter-price],[data-order-qty]') && !this.paused) {
        this.paused = true;
        this.minuteAccumulator = 0;
        this.renderHeaderOnly();
        this.toast('Clock paused while you plan. Run a shift or choose Live when ready.');
      }
    });
    this.root.addEventListener('change', event => this.onChange(event));
    this.root.addEventListener('input', event => { if(event.target.matches('[data-order-qty]')) updateOrderQuotes(this.root); });
    this.root.addEventListener('dragstart', event => this.onDragStart(event));
    this.root.addEventListener('dragover', event => this.onDragOver(event));
    this.root.addEventListener('dragleave', event => this.onDragLeave(event));
    this.root.addEventListener('drop', event => this.onDrop(event));
    this.root.querySelector('[data-import-file]').addEventListener('change', event => this.importFile(event));
    document.addEventListener('keydown', event => {
      if (this.casino) return;
      const card = event.target.closest?.('.drag-card[role="button"]');
      if (card && ['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        card.click();
        return;
      }
      const tab = event.target.closest?.('[role="tab"]');
      if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const tabs = [...this.root.querySelectorAll('[role="tab"]')];
        const current = tabs.indexOf(tab);
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        this.navigateLedger(tabs[index].dataset.tab, { restore: true });
        this.root.querySelector(`[data-tab="${this.activeTab}"]`)?.focus();
        return;
      }
      if (event.key === 'Escape') {
        if (this.root.querySelector('[data-modal]')) {
          this.root.querySelector('[data-modal]').remove();
          return;
        }
        if (this.selection) {
          this.selection = null;
          this.closePanels();
          this.render(true);
          return;
        }
        if (this.workspaceOpen || this.arrivalsOpen) {
          this.closePanels();
          return;
        }
        this.selection = null;
        this.sceneMode = 'river';
        this.interiorPlotIndex = null;
        this.root.querySelector('[data-modal]')?.remove();
        this.render(true);
      }
      if (event.code === 'Space' && !event.target.matches('input,button,select')) {
        event.preventDefault();
        this.paused = !this.paused;
        this.renderHeaderOnly();
      }
    });
    window.addEventListener('message', event => this.onCasinoMessage(event));
    window.addEventListener('resize', () => this.centerWorld());
    window.addEventListener('beforeunload', () => this.persist());
  }

  async begin(newGame) {
    this.sceneMode = 'river';
    this.interiorPlotIndex = null;
    this.activeTab = 'desk';
    this.selection = null;
    this.marketFilter = 'shelf';
    this.ledgerViews.clear();
    this.sectionTabs = Object.fromEntries(SECTIONS.map(section => [section.id, section.tabs[0]]));
    this.paused = true;
    this.minuteAccumulator = 0;
    this.shiftResult = null;
    if (newGame) {
      if (this.hasStoredGame) localStorage.setItem('trading-post-backup-before-new-journey', serializeState(this.state));
      clearSavedGame();
      this.state = createInitialState(`trading-post-${Date.now()}`);
      this.lastLedgerLength = 0;
      this.hasStoredGame = false;
    }
    try {
      await audio.start();
      audio.setMuted(this.muted);
      await audio.setSoundtrack(this.soundtrack);
    } catch {
      this.toast('Audio could not start. The town is ready to play.', 'bad');
    }
    this.running = true;
    this.closePanels(false);
    this.root.querySelector('.start-screen')?.remove();
    this.persist();
    this.render(true);
    this.centerWorld();
    audio.openingChime();
    if (this.state.opening.status === 'completed') this.toast('Welcome back. Your settlement plan is ready.', 'good');
  }

  mutate(action, successMessage) {
    try {
      const oldLedgerId = this.state.nextLedgerId;
      const oldCash = this.state.cashCents;
      action();
      const trades = this.state.ledger.filter(entry=>Number(entry.id.replace('ledger-','')) >= oldLedgerId && entry.accepted && ['buy','meeting-buy','sell','meeting-sell'].includes(entry.kind));
      if (trades.length && !this.inWorkShift) {
        const sold = trades.filter(entry=>['sell','meeting-sell'].includes(entry.kind));
        const bought = trades.filter(entry=>['buy','meeting-buy'].includes(entry.kind));
        const item = entry => ({goodId:entry.goodId,name:GOODS.find(good=>good.id===entry.goodId)?.name || entry.goodId,qty:entry.qty,totalCents:Math.abs(entry.totalCents),costBasisCents:entry.costBasisCents,grossProfitCents:entry.grossProfitCents});
        this.shiftResult = {elapsedMinutes:0,cashDeltaCents:this.state.cashCents-oldCash,soldGoods:sold.map(item),purchasedGoods:bought.map(item),soldQty:sold.reduce((n,e)=>n+e.qty,0),boughtQty:bought.reduce((n,e)=>n+e.qty,0),salesCents:sold.reduce((n,e)=>n+Math.abs(e.totalCents),0),stockSpendCents:bought.reduce((n,e)=>n+Math.abs(e.totalCents),0),grossProfitKnown:sold.every(e=>Number.isInteger(e.grossProfitCents)),grossProfitCents:sold.reduce((n,e)=>n+(e.grossProfitCents||0),0),notes:[]};
      }
      this.persist();
      this.render(true);
      if (successMessage) this.toast(successMessage, 'good');
      return true;
    } catch (error) {
      this.toast(error.message, 'bad');
      this.render(true);
      return false;
    }
  }

  rememberLedgerView() {
    const content = this.root.querySelector('.ledger-content');
    if (!content) return;
    const absoluteTop = content.getBoundingClientRect().top + window.scrollY;
    const header = this.root.querySelector('.topbar').getBoundingClientRect().height;
    this.ledgerViews.set(this.activeTab, { desktop: content.scrollTop, mobile: Math.max(0, window.scrollY - absoluteTop + header + 8) });
    const section = SECTIONS.find(item => item.tabs.includes(this.activeTab));
    if (section) this.sectionTabs[section.id] = this.activeTab;
  }

  navigateLedger(tab, { restore = false, clearSelection = true } = {}) {
    this.rememberLedgerView();
    this.activeTab = tab;
    this.workspaceOpen = true;
    this.arrivalsOpen = false;
    const section = SECTIONS.find(item => item.tabs.includes(tab));
    if (section) this.sectionTabs[section.id] = tab;
    if (clearSelection) this.selection = null;
    this.render(true);
    const content = this.root.querySelector('.ledger-content');
    const saved = restore ? this.ledgerViews.get(tab) : null;
    content.scrollTop = saved?.desktop || 0;
    this.root.querySelector(`[data-tab="${tab}"]`)?.focus({ preventScroll: true });
  }

  syncPanels() {
    const section = SECTIONS.find(item => item.tabs.includes(this.activeTab)) || SECTIONS[0];
    this.root.querySelector('.ledger-deck').hidden = !this.workspaceOpen;
    this.root.querySelector('.arrival-panel').hidden = !this.arrivalsOpen;
    this.root.querySelector('[data-workspace-title]').textContent = {desk:'Trading counter',shop:'Stock & orders',land:'Land & buildings',records:'Town records'}[section.id];
    this.root.querySelectorAll('[data-open-workspace]').forEach(button => {
      const active = this.workspaceOpen && button.dataset.openWorkspace === section.id;
      button.setAttribute('aria-expanded', String(active));
      button.classList.toggle('active', active);
    });
    this.root.querySelector('[data-action="toggle-arrivals"]').setAttribute('aria-expanded', String(this.arrivalsOpen));
  }

  closePanels(focus = true) {
    this.rememberLedgerView();
    this.workspaceOpen = false;
    this.arrivalsOpen = false;
    this.syncPanels();
    if (focus) {
      const section = SECTIONS.find(item => item.tabs.includes(this.activeTab));
      this.root.querySelector(`[data-open-workspace="${section?.id || 'desk'}"]`)?.focus({ preventScroll: true });
    }
  }

  centerWorld(plotIndex = 3) {
    const host = this.root.querySelector('.world-scene');
    const camera = host?.querySelector('[data-world-camera]');
    if (!camera) return;
    const x = (120 + (plotIndex - 1) * 190) / 1000 * camera.offsetWidth;
    host.scrollTo({ left: Math.max(0, x - host.clientWidth / 2), top: Math.max(0, (camera.offsetHeight - host.clientHeight) * .35), behavior: 'instant' });
  }

  onClick(event) {
    const target = event.target.closest('button,[data-place-facility],[data-place-accessory],[data-place-dock],[data-plot]');
    if (!target) return;
    const action = target.dataset.action;
    if (action === 'continue') return void this.begin(false);
    if (action === 'new-game') return void this.begin(true);
    if (target.dataset.openWorkspace) {
      const section = SECTIONS.find(item => item.id === target.dataset.openWorkspace);
      if (this.workspaceOpen && section.tabs.includes(this.activeTab)) return this.closePanels();
      if (section.id === 'land') { this.paused = true; this.minuteAccumulator = 0; }
      return this.navigateLedger(this.sectionTabs[section.id] || section.tabs[0], { restore: true });
    }
    if (action === 'close-workspace' || action === 'close-arrivals') return this.closePanels();
    if (action === 'toggle-arrivals') {
      this.workspaceOpen = false;
      this.arrivalsOpen = !this.arrivalsOpen;
      this.syncPanels();
      if (this.arrivalsOpen) this.root.querySelector('[data-action="close-arrivals"]').focus({ preventScroll: true });
      return;
    }
    if (target.dataset.panWorld) {
      const host = this.root.querySelector('.world-scene');
      const direction = target.dataset.panWorld;
      host.scrollTo({ left: direction === 'left' ? host.scrollLeft - host.clientWidth * .65 : direction === 'right' ? host.scrollLeft + host.clientWidth * .65 : host.scrollLeft, top: direction === 'land' ? 0 : direction === 'river' ? host.scrollHeight : host.scrollTop, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      return;
    }
    if (action === 'pause') {
      this.paused = !this.paused;
      this.renderHeaderOnly();
      return;
    }
    if (action === 'speed') {
      this.speed = this.speed === 1 ? 2 : this.speed === 2 ? 4 : 1;
      this.renderHeaderOnly();
      this.toast(`Town clock set to ${this.speed}×.`);
      return;
    }
    if (action === 'mute') {
      this.muted = !this.muted;
      audio.setMuted(this.muted);
      this.renderHeaderOnly();
      return;
    }
    if (target.dataset.section) {
      const section = SECTIONS.find(item => item.id === target.dataset.section);
      if (section.id === 'land') { this.paused = true; this.minuteAccumulator = 0; }
      this.navigateLedger(this.sectionTabs[section.id] || section.tabs[0], { restore: true });
      return;
    }
    if (action === 'expanded-shelf') return this.mutate(() => configureExpandedShelf(this.state), 'Your five-good shelf is ready. Prices follow the market with bounded stock and working cash.');
    if (action === 'starter-shelf') {
      return this.mutate(() => configureStarterShelf(this.state), 'Your three reliable goods are ready. Serve customers to buy, sell and reinvest.');
    }
    if (action === 'work-shift') return this.workShift();
    if (action === 'work-day') return this.workShift(true);
    if (action === 'follow-shelf-market') {
      this.paused = true;
      this.minuteAccumulator = 0;
      const goods = getDerived(this.state).ledgerRows.filter(row => row.unlocked && row.displayed && (row.qty > 0 || row.buyCeilingCents > 0));
      return this.mutate(() => { for (const row of goods) setRestockPolicy(this.state,row.id,{pricingMode:'market'}); }, `${goods.length} shelf goods follow the market. Your limits and working cash are kept.`);
    }
    if (target.dataset.pricingGood) {
      this.paused = true;
      this.minuteAccumulator = 0;
      const goodId=target.dataset.pricingGood;
      this.mutate(() => setRestockPolicy(this.state,goodId,{pricingMode:target.dataset.pricingMode}), target.dataset.pricingMode === 'market' ? 'Buying and selling follow the market; limits and cash reserve stay in place.' : 'These prices stay fixed until you edit them.');
      this.root.querySelector(`[data-pricing-good="${CSS.escape(goodId)}"][data-pricing-mode="${target.dataset.pricingMode}"]`)?.focus({preventScroll:true});
      return;
    }
    if (target.dataset.focusProject) {
      this.paused = true;
      this.minuteAccumulator = 0;
      this.selectedPlotIndex = Number(target.dataset.projectPlot);
      this.navigateLedger('build');
      const card = this.root.querySelector(`[data-project-card="${CSS.escape(target.dataset.focusProject)}"]`);
      if (card?.closest('details')) card.closest('details').open = true;
      card?.classList.add('focused-project');
      card?.scrollIntoView({block:'center'});
      if (window.innerWidth > 900) window.scrollTo({top:0,behavior:'instant'});
      card?.querySelector('button')?.focus({preventScroll:true});
      return;
    }
    if (action === 'show-world') { this.closePanels(); return; }
    if (action === 'live-mode') { this.root.querySelector('[data-modal="help"]')?.remove(); this.paused = false; this.renderHeaderOnly(); return; }
    if (action === 'help') {
      this.paused = true;
      this.renderHeaderOnly();
      if (!this.root.querySelector('[data-modal="help"]')) this.root.insertAdjacentHTML('beforeend', helpModalHtml(this.soundtrack));
      return;
    }
    if (action === 'journey-next') {
      this.workToast?.remove();
      const current = settlementJourney(this.state, getDerived(this.state)).current;
      const next = current.action;
      if (current.id === 'first-trade') {
        this.activeTab = 'desk';
        this.workspaceOpen = true;
        this.paused = true;
        this.minuteAccumulator = 0;
        this.render(true);
        const buy = this.root.querySelector('[data-meeting-accept]');
        buy?.scrollIntoView({block:'center'});
        buy?.focus({preventScroll:true});
        return;
      }
      if (next.type === 'intro-shelf') {
        this.activeTab = 'desk';
        return this.mutate(() => configureIntroShelf(this.state), 'Your pelts are on sale. Serve the outfitter to complete your first customer sale.');
      }
      if (next.type === 'starter-shelf') {
        this.activeTab = 'desk';
        return this.mutate(() => configureStarterShelf(this.state), 'Pelts, rope and provisions are ready to buy and sell. Limited restocking protects your working cash.');
      }
      if (next.type === 'next-opportunity') return this.workShift();
      this.activeTab = next.tab || 'desk';
      this.workspaceOpen = true;
      if (next.offerId) {
        this.paused = true;
        this.minuteAccumulator = 0;
      }
      if (next.filter) this.marketFilter = next.filter;
      if (next.plotIndex) this.selectedPlotIndex = next.plotIndex;
      if (next.facilityId && this.activeTab !== 'build') {
        const facility = FACILITIES.find(item => item.id === next.facilityId);
        if (facility) this.paused = true;
      this.minuteAccumulator = 0;
      this.selection = { kind: 'facility', id: facility.id, name: facility.name };
      } else if (next.accessoryId) {
        const accessory = ACCESSORIES.find(item => item.id === next.accessoryId);
        if (accessory) this.paused = true;
      this.minuteAccumulator = 0;
      this.selection = { kind: 'accessory', id: accessory.id, name: accessory.name };
      }
      this.render(true);
      this.root.querySelector('.ledger-content').scrollTop = 0;
      if (this.activeTab === 'build' || this.activeTab === 'decor') this.root.querySelector('.placement-guide')?.scrollIntoView({ block: 'nearest' });
      if (next.focus === 'growth') { const choices = this.root.querySelector('.growth-planner'); choices?.scrollIntoView({block:'start'}); if (window.innerWidth > 900) window.scrollTo({top:0,behavior:'instant'}); choices?.querySelector('button[data-focus-project]')?.focus({preventScroll:true}); }
      if (next.offerId) this.root.querySelector(`[data-offer-card="${next.offerId}"]`)?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (action === 'cancel-placement') {
      this.selection = null;
      this.render(true);
      return;
    }
    if (target.dataset.placeSelectedPlot) return this.placeSelection(Number(target.dataset.placeSelectedPlot), .28, .88);
    if (target.dataset.recommendFacility || target.dataset.recommendAccessory) {
      const kind = target.dataset.recommendFacility ? 'facility' : 'accessory';
      const item = (kind === 'facility' ? FACILITIES : ACCESSORIES).find(item => item.id === (target.dataset.recommendFacility || target.dataset.recommendAccessory));
      if (!item || item.rank > this.state.rank) return;
      this.paused = true;
      this.minuteAccumulator = 0;
      this.selection = { kind, id: item.id, name: item.name };
      this.render(true);
      this.root.querySelector('.placement-guide')?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (target.dataset.deskTab) {
      if (target.dataset.deskFilter) this.marketFilter = target.dataset.deskFilter;
      this.navigateLedger(target.dataset.deskTab);
      return;
    }
    if (target.dataset.marketFilter) {
      this.marketFilter = target.dataset.marketFilter;
      this.render(true);
      this.root.querySelector(`[data-market-filter="${this.marketFilter}"]`)?.focus({ preventScroll: true });
      return;
    }
    if (target.dataset.suggestPrice) {
      const goodId = target.dataset.suggestPrice;
      const row = getDerived(this.state).ledgerRows.find(item => item.id === goodId);
      if (!row?.unlocked) return;
      const price = Number(target.dataset.suggestCents);
      this.mutate(() => setPrice(this.state, goodId, price), `${row.name} sell price set to ${money(price)}.`);
      this.root.querySelector(`[data-suggest-price="${goodId}"]`)?.focus({ preventScroll: true });
      return;
    }
    if (action === 'open-meetings') {
      this.navigateLedger('meetings');
      return;
    }
    if (action === 'open-poker') {
      const plotIndex = this.interiorPlotIndex;
      const derived = getDerived(this.state);
      if (!derived.casino.open) return this.toast(derived.casino.blockReason || 'The casino is not ready to open.', 'bad');
      if (this.state.poker.activeSession) return void this.openPokerTable(plotIndex);
      if (!this.root.querySelector('[data-modal="poker-buy-in"]')) {
        const minimum = derived.casino.tableStakes.minCents;
        const maximum = Math.min(derived.casino.tableStakes.maxCents, this.state.cashCents);
        const suggested = Math.max(minimum, Math.min(maximum, 1000));
        this.root.insertAdjacentHTML('beforeend', `<div class="modal-backdrop" data-modal="poker-buy-in">
          <section class="modal-card poker-buy-in-card" role="dialog" aria-modal="true" aria-labelledby="poker-buy-in-title">
            <button class="plate-button modal-close" data-action="close-modal">CLOSE</button>
            <p class="eyebrow">RIVER CASINO · FICTIONAL CURRENCY</p><h2 id="poker-buy-in-title">TAKE NOTES TO THE TABLE</h2>
            <p>Only your earned game notes are used at this table. Unfinished hands remain reserved for resumption.</p>
            <label>BUY-IN / NOTES <span>${money(minimum)}–${money(maximum)}</span><input data-poker-buy-in type="number" min="${(minimum / 100).toFixed(2)}" max="${(maximum / 100).toFixed(2)}" step="0.25" value="${(suggested / 100).toFixed(2)}"></label>
            <button class="plate-button" data-action="confirm-poker-buy-in" data-poker-plot="${plotIndex}">RESERVE BUY-IN &amp; ENTER</button>
          </section></div>`);
      }
      return;
    }
    if (action === 'confirm-poker-buy-in') {
      const input = this.root.querySelector('[data-poker-buy-in]');
      const buyInCents = Math.round(Number.parseFloat(input?.value) * 100);
      const plotIndex = Number(target.dataset.pokerPlot);
      const opened = this.mutate(() => beginPokerSession(this.state, buyInCents, {
        plotIndex,
        maxExposureCents: 100_000,
      }));
      if (!opened) return;
      target.closest('[data-modal]')?.remove();
      return void this.openPokerTable(plotIndex);
    }
    if (action === 'close-shop-view') {
      this.sceneMode = 'river';
      this.interiorPlotIndex = null;
      this.render(true);
      return;
    }
    if (target.dataset.interiorChoice) {
      const plotIndex = Number(target.dataset.interiorPlot);
      const resolved = this.mutate(() => resolveInteriorOpportunity(this.state, plotIndex, target.dataset.interiorChoice));
      if (resolved) {
        const entry = this.state.ledger.at(-1);
        this.toast(entry?.reason || 'The interior opportunity was resolved and entered in the ledger.', entry?.totalCents < 0 ? '' : 'good');
      }
      return;
    }
    if (target.dataset.shopGood || target.dataset.focusGood) {
      const goodId = target.dataset.shopGood || target.dataset.focusGood;
      this.marketFilter = 'all';
      this.navigateLedger('market');
      const row = this.root.querySelector(`[data-good-row="${CSS.escape(goodId)}"]`);
      row?.classList.add('focused-stock-row');
      row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const good = GOODS.find(item => item.id === goodId);
      this.toast(`${good?.name || goodId}: price, display, and automatic trade controls are open in the ledger.`);
      return;
    }
    if (action === 'close-modal') {
      target.closest('[data-modal]')?.remove();
      return;
    }
    if (action === 'export-save') return this.exportSave();
    if (action === 'import-save') return this.root.querySelector('[data-import-file]').click();
    if (target.dataset.tab) {
      this.navigateLedger(target.dataset.tab, { restore: true });
      return;
    }
    if (target.dataset.orderStep) {
      const input = this.root.querySelector(`[data-order-qty="${CSS.escape(target.dataset.orderStep)}"]`);
      if (!input || input.disabled) return;
      const minimum = Number.parseInt(input.min, 10) || 1;
      const maximum = Number.parseInt(input.max, 10) || 99;
      const current = Number.parseInt(input.value, 10);
      const delta = Number.parseInt(target.dataset.orderDelta, 10) || 0;
      input.value = String(Math.max(minimum, Math.min(maximum, (Number.isInteger(current) ? current : minimum) + delta)));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus({ preventScroll: true });
      return;
    }
    if (target.dataset.order) {
      const [routeId, goodId] = target.dataset.order.split(':');
      const quantityInput = this.root.querySelector(`[data-order-qty="${CSS.escape(`${routeId}:${goodId}`)}"]`);
      const quantity = Number.parseInt(quantityInput?.value, 10);
      this.mutate(() => orderFromRoute(this.state, routeId, goodId, quantity), `${goodId.replaceAll('-', ' ')} ordered from ${routeId === 'cuba' ? 'Havana' : 'Chicago'}.`);
      return;
    }
    if (target.dataset.meetingAccept) {
      const offerId = target.dataset.meetingAccept;
      const openingTrade = this.state.opening.status !== 'completed';
      const accepted = this.mutate(() => acceptMeetingOffer(this.state, offerId), openingTrade ? 'Your pelts are on sale. Serve the outfitter to receive your first trading profit.' : 'The negotiated deal was entered in the ledger.');
      if (accepted && openingTrade) {
        this.openingToast?.remove();
        this.activeTab = 'desk';
        this.render(true);
      }
      return;
    }
    if (target.dataset.meetingCounter) {
      const offerId = target.dataset.meetingCounter;
      const input = this.root.querySelector(`[data-counter-price="${CSS.escape(offerId)}"]`);
      const cents = Math.round(Number.parseFloat(input?.value) * 100);
      this.mutate(() => counterMeetingOffer(this.state, offerId, cents), 'Your counteroffer was presented.');
      return;
    }
    if (target.dataset.meetingDecline) {
      this.mutate(() => declineMeetingOffer(this.state, target.dataset.meetingDecline), 'The meeting ended without a deal.');
      return;
    }
    if (target.dataset.buildPlot) {
      this.selectedPlotIndex = Number(target.dataset.buildPlot);
      this.selection = null;
      this.render(true);
      return;
    }
    if (target.dataset.projectBundle) {
      const plotIndex = Number(target.dataset.projectPlot);
      const facilityId = target.dataset.projectBundle;
      this.paused = true;
      this.minuteAccumulator = 0;
      return this.mutate(() => {
        startPlotProject(this.state,plotIndex,facilityId,{cashReserveCents:Number(target.dataset.projectReserve)||0});
        this.selectedPlotIndex = plotIndex;
        this.selection = null;
        this.activeTab = 'desk';
      }, `${FACILITIES.find(item=>item.id===facilityId)?.name} commissioned on Plot ${plotIndex}. Serve customers while your crew works.`);
    }
    if (target.dataset.purchasePlot) {
      const plotIndex = Number(target.dataset.purchasePlot);
      this.mutate(() => purchasePlot(this.state, plotIndex), `Plot ${plotIndex} is yours. Choose a starter project below, then BUILD ON PLOT ${plotIndex}.`);
      return;
    }
    if (target.dataset.purchaseDock) {
      this.mutate(() => purchaseDockTreatment(this.state, target.dataset.purchaseDock), 'Dock treatment purchased. Select it below, then choose a river plot.');
      return;
    }
    if (target.dataset.hireEmployee) {
      this.mutate(() => hireEmployee(this.state, target.dataset.hireEmployee), 'Employee hired with a two-day wage advance.');
      return;
    }
    if (target.dataset.fireEmployee) {
      this.mutate(() => fireEmployee(this.state, target.dataset.fireEmployee), 'Employment ended and recorded.');
      return;
    }
    if (target.dataset.auditEmployee) {
      this.mutate(() => auditEmployee(this.state, target.dataset.auditEmployee), 'Audit completed from the employee record.');
      return;
    }
    if (target.dataset.repairPlot) {
      const plotIndex = Number(target.dataset.repairPlot);
      const damageId = target.dataset.damageId || null;
      this.mutate(() => repairProperty(this.state, plotIndex, damageId), `Plot ${plotIndex} repairs completed.`);
      return;
    }
    if (target.dataset.placeFacility) {
      const facility = FACILITIES.find(item => item.id === target.dataset.placeFacility);
      if (facility?.rank > this.state.rank) return this.toast(`${facility.name} is not unlocked yet.`, 'bad');
      this.paused = true;
      this.minuteAccumulator = 0;
      this.selection = { kind: 'facility', id: facility.id, name: facility.name };
      this.activeTab = 'build';
      this.render(true);
      this.root.querySelector('.placement-guide')?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (target.dataset.placeAccessory) {
      const accessory = ACCESSORIES.find(item => item.id === target.dataset.placeAccessory);
      if (accessory?.rank > this.state.rank) return this.toast(`${accessory.name} is not unlocked yet.`, 'bad');
      this.paused = true;
      this.minuteAccumulator = 0;
      this.selection = { kind: 'accessory', id: accessory.id, name: accessory.name };
      this.activeTab = 'decor';
      this.render(true);
      this.root.querySelector('.placement-guide')?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (target.dataset.placeDock) {
      const owned = this.state.dockTreatments.find(item => item.id === target.dataset.placeDock);
      if (!owned) return;
      const treatment = getDerived(this.state).dockTreatments.catalog.find(item => item.id === owned.treatmentId);
      this.paused = true;
      this.minuteAccumulator = 0;
      this.selection = { kind: 'dock', id: owned.id, name: treatment?.name || 'dock treatment' };
      this.activeTab = 'decor';
      this.render(true);
      return;
    }
    if (target.dataset.plot && !this.selection) {
      const plotIndex = Number(target.dataset.plot);
      const plot = this.state.plots[plotIndex - 1];
      if (plot?.owned === false || plot?.owned && !INTERIOR_FACILITIES.has(plot.facilityId)) {
        this.activeTab = 'build';
        this.workspaceOpen = true;
        this.selectedPlotIndex = plotIndex;
        this.paused = true;
        this.minuteAccumulator = 0;
        this.render(true);
        this.root.querySelector('.plot-context')?.scrollIntoView({ block: 'nearest' });
        return;
      }
      if (plot?.owned && !plot.construction && INTERIOR_FACILITIES.has(plot.facilityId)) {
        this.closePanels(false);
        this.sceneMode = 'interior';
        this.interiorPlotIndex = plotIndex;
        this.render(true);
        this.toast(`${FACILITIES.find(item => item.id === plot.facilityId)?.name || 'Building'} interior opened. Trade and stock panels are available in the dock.`);
        return;
      }
    }
    if (target.dataset.plot && this.selection) {
      const rect = target.getBoundingClientRect();
      this.placeSelection(Number(target.dataset.plot), .5, Math.max(.2, Math.min(.9, (event.clientY - rect.top) / rect.height)));
    }
  }

  openPokerTable(plotIndex = null) {
    if (this.casino) return;
    const session = this.state.poker.activeSession;
    if (!session) return this.toast('Choose a table buy-in first.', 'bad');
    try {
      if (session.status === 'frozen') resumePokerSession(this.state, session.sessionId, session.nonce);
      this.persist();
    } catch (error) {
      this.toast(error.message, 'bad');
      return;
    }
    this.wasPausedBeforeCasino = this.paused;
    this.paused = true;
    audio.setMuted(true);
    const overlay = document.createElement('section');
    overlay.className = 'casino-overlay';
    overlay.setAttribute('aria-label', 'River Casino poker table');
    overlay.innerHTML = `<div class="casino-loading" role="status"><b>OPENING THE TABLE</b><span>Your reserved cash is being verified…</span></div><iframe title="River Casino poker table" src="./casino/index.html" sandbox="allow-scripts allow-same-origin" allow="autoplay"></iframe>`;
    document.body.append(overlay);
    const iframe = overlay.querySelector('iframe');
    this.casino = { overlay, iframe, sessionId: session.sessionId, nonce: session.nonce, initialized: false, plotIndex: plotIndex || session.plotIndex };
    iframe.addEventListener('load', () => iframe.focus(), { once: true });
    iframe.addEventListener('error', () => {
      this.toast('The poker room could not be opened. Your reserved buy-in remains safe.', 'bad');
      this.closeCasinoOverlay();
    }, { once: true });
  }

  postCasino(type, fields = {}) {
    const casino = this.casino;
    if (!casino?.iframe.contentWindow) return;
    casino.iframe.contentWindow.postMessage({ protocol: CASINO_PROTOCOL, type, runtimeVersion: '1.0.0', sessionId: casino.sessionId, nonce: casino.nonce, ...fields }, window.location.origin);
  }

  onCasinoMessage(event) {
    const casino = this.casino;
    if (!casino || event.origin !== window.location.origin || event.source !== casino.iframe.contentWindow) return;
    const message = event.data;
    if (!message || message.protocol !== CASINO_PROTOCOL) return;
    const session = this.state.poker.activeSession;
    if (message.type === 'READY') {
      if (casino.initialized || !session || session.sessionId !== casino.sessionId || session.nonce !== casino.nonce) return;
      casino.initialized = true;
      casino.overlay.querySelector('.casino-loading')?.remove();
      const resume = session.engineSnapshot ? {
        revision: session.revision,
        initialEscrowCents: session.buyInCents,
        state: session.engineSnapshot,
      } : undefined;
      this.postCasino('INIT', { escrowCents: session.buyInCents, ...(resume ? { resume } : {}) });
      return;
    }
    if (!session || message.sessionId !== session.sessionId || message.nonce !== session.nonce) return;
    try {
      if (message.type === 'CHECKPOINT') {
        if (!Number.isInteger(message.revision) || !Number.isInteger(message.stackCents) || !message.state) throw new Error('Poker sent an incomplete checkpoint');
        checkpointPokerSession(this.state, session.sessionId, session.nonce, {
          revision: message.revision,
          currentStackCents: message.stackCents,
          maxExposureCents: session.maxExposureCents,
          engineSnapshot: message.state,
        });
        this.persist();
        this.postCasino('ACK', { revision: message.revision });
        return;
      }
      if (message.type === 'CLOSE_REQUEST') {
        if (message.revision !== session.revision) throw new Error('Poker close request did not match the saved checkpoint');
        if (message.safeToClose && CASINO_SAFE_PHASES.has(message.phase)) {
          endPokerSession(this.state, session.sessionId, session.nonce, message.revision);
          this.persist();
          this.toast('Poker cash returned to the Trading Post ledger.', 'good');
        } else {
          freezePokerSession(this.state, session.sessionId, session.nonce, 'Unfinished poker hand reserved for exact resumption');
          this.persist();
          this.toast('The unfinished hand and buy-in are reserved. Reopen the casino to resume.', 'good');
        }
        this.closeCasinoOverlay();
        return;
      }
      if (message.type === 'ERROR') {
        if (session.engineSnapshot && session.status !== 'frozen') freezePokerSession(this.state, session.sessionId, session.nonce, 'Poker table paused after a recoverable runtime error');
        this.persist();
        this.toast('Poker paused safely. Reopen the casino to resume the saved table.', 'bad');
        this.closeCasinoOverlay();
      }
    } catch (error) {
      if (session.engineSnapshot && session.status !== 'frozen') {
        try { freezePokerSession(this.state, session.sessionId, session.nonce, `Poker host rejected unsafe state: ${error.message}`); } catch {}
      }
      this.persist();
      this.toast(`Poker paused: ${error.message}`, 'bad');
      this.closeCasinoOverlay();
    }
  }

  closeCasinoOverlay() {
    if (!this.casino) return;
    this.postCasino('SET_ACTIVE', { active: false });
    this.casino.overlay.remove();
    this.casino = null;
    this.paused = this.wasPausedBeforeCasino;
    audio.setMuted(this.muted);
    this.render(true);
  }

  onChange(event) {
    const target = event.target;
    if (target.dataset.sellPrice) {
      const cents = Math.round(Number.parseFloat(target.value) * 100);
      this.mutate(() => setPrice(this.state, target.dataset.sellPrice, cents));
    } else if (target.dataset.buyCeiling) {
      const cents = Math.round(Number.parseFloat(target.value) * 100);
      this.mutate(() => setBuyCeiling(this.state, target.dataset.buyCeiling, cents));
    } else if (target.dataset.restockTarget) {
      const qty=target.value.trim() === '' ? null : Number(target.value);
      this.mutate(() => setRestockPolicy(this.state,target.dataset.restockTarget,{targetQty:qty}));
    } else if (target.dataset.restockReserve) {
      const cents=Math.round(Number.parseFloat(target.value)*100);
      this.mutate(() => setRestockPolicy(this.state,target.dataset.restockReserve,{cashReserveCents:cents}));
    } else if (target.dataset.displayGood) {
      this.mutate(() => setDisplay(this.state, target.dataset.displayGood, target.checked));
    } else if (target.dataset.meetingPolicy !== undefined) {
      this.mutate(() => setMeetingPolicy(this.state, target.value), `Meeting filter set to ${target.selectedOptions[0]?.textContent || target.value}.`);
    } else if (target.dataset.soundtrack !== undefined) {
      this.soundtrack = target.value;
      localStorage.setItem('trading-post-soundtrack', this.soundtrack);
      audio.setSoundtrack(this.soundtrack);
      this.toast(`Soundtrack: ${target.selectedOptions[0]?.textContent || this.soundtrack}.`, 'good');
    } else if (target.dataset.employeeAssignment !== undefined) {
      this.mutate(() => assignEmployee(this.state, target.dataset.employeeAssignment, target.value), 'Employee assignment updated.');
    }
  }

  workShift(fullDay = false) {
    if (this.state.opening.status !== 'completed') return;
    this.paused = true;
    this.minuteAccumulator = 0;
    this.rememberLedgerView();
    this.activeTab = 'desk';
    this.sectionTabs.desk = 'desk';
    const oldLedgerLength = this.state.ledger.length;
    this.inWorkShift = true;
    this.mutate(() => { this.shiftResult = fullDay ? runTradingDay(this.state) : runWorkShift(this.state); });
    this.inWorkShift = false;
    this.reactToSimulation(oldLedgerLength);
    this.root.querySelector('[data-shift-result]')?.scrollIntoView({block:'start'});
    if (window.innerWidth > 900) window.scrollTo({top:0,behavior:'instant'});
  }

  onDragStart(event) {
    const facilityCard = event.target.closest('[data-place-facility]');
    const accessoryCard = event.target.closest('[data-place-accessory]');
    const decoration = event.target.closest('[data-decoration-id]');
    const dockCard = event.target.closest('[data-place-dock]');
    const dockPlaced = event.target.closest('[data-dock-treatment-id]');
    let payload;
    if (facilityCard) payload = { kind: 'facility', id: facilityCard.dataset.placeFacility };
    else if (accessoryCard) payload = { kind: 'accessory', id: accessoryCard.dataset.placeAccessory };
    else if (decoration) payload = { kind: 'move', id: decoration.dataset.decorationId };
    else if (dockCard) payload = { kind: 'dock', id: dockCard.dataset.placeDock };
    else if (dockPlaced) payload = { kind: 'dock-move', id: dockPlaced.dataset.dockTreatmentId };
    if (!payload) return;
    event.dataTransfer.effectAllowed = payload.kind === 'move' || payload.kind === 'dock-move' ? 'move' : 'copy';
    event.dataTransfer.setData('application/x-trading-post', JSON.stringify(payload));
    event.dataTransfer.setData('text/plain', `${payload.kind}:${payload.id}`);
  }

  onDragOver(event) {
    const plot = event.target.closest('[data-plot]');
    if (!plot) return;
    event.preventDefault();
    plot.classList.add('drop-ready');
  }

  onDragLeave(event) {
    const plot = event.target.closest('[data-plot]');
    if (!plot || plot.contains(event.relatedTarget)) return;
    plot.classList.remove('drop-ready');
  }

  onDrop(event) {
    const plot = event.target.closest('[data-plot]');
    if (!plot) return;
    event.preventDefault();
    plot.classList.remove('drop-ready');
    let payload;
    try {
      payload = JSON.parse(event.dataTransfer.getData('application/x-trading-post'));
    } catch {
      return;
    }
    const rect = plot.getBoundingClientRect();
    const x = Math.max(.05, Math.min(.95, (event.clientX - rect.left) / rect.width));
    const y = Math.max(.2, Math.min(.9, (event.clientY - rect.top) / rect.height));
    const plotIndex = Number(plot.dataset.plot);
    if (payload.kind === 'move') {
      this.mutate(() => moveAccessory(this.state, payload.id, plotIndex, x, y), 'Accessory moved.');
      return;
    }
    if (payload.kind === 'dock-move') {
      this.mutate(() => moveDockTreatment(this.state, payload.id, plotIndex, x, y), 'Dock treatment moved.');
      return;
    }
    this.selection = { ...payload, name: payload.id };
    this.placeSelection(plotIndex, x, y);
  }

  placeSelection(plotIndex, x, y) {
    const selection = this.selection;
    if (!selection) return;
    this.workToast?.remove();
    const success = this.mutate(() => {
      if (selection.kind === 'facility') startConstruction(this.state, plotIndex, selection.id);
      else if (selection.kind === 'dock') placeDockTreatment(this.state, selection.id, plotIndex, x, y);
      else placeAccessory(this.state, selection.id, plotIndex, x, y);
    }, `${selection.name} placed on Plot ${plotIndex}.`);
    if (success) {
      audio.hammer();
      this.selection = null;
      this.render(true);
    }
  }

  frame(time) {
    if (!this.lastFrame) this.lastFrame = time;
    const elapsedSeconds = Math.min(.5, (time - this.lastFrame) / 1000);
    this.lastFrame = time;
    if (this.running && !this.paused && !this.root.querySelector('[data-modal]') && !this.selection && elapsedSeconds > 0) {
      const oldLedger = this.state.ledger.length;
      const oldMinute = this.state.timeMinutes;
      this.minuteAccumulator += elapsedSeconds * 1.5 * this.speed;
      const gameMinutes = Math.floor(this.minuteAccumulator);
      if (gameMinutes > 0) {
        this.minuteAccumulator -= gameMinutes;
        tick(this.state, gameMinutes);
        this.reactToSimulation(oldLedger);
        if (Math.floor(oldMinute / 60) !== Math.floor(this.state.timeMinutes / 60)) this.persist();
      }
    }
    if (time - this.lastRender > 400) {
      this.render(false);
      this.lastRender = time;
    }
    requestAnimationFrame(next => this.frame(next));
  }

  reactToSimulation(oldLedgerLength) {
    const additions = this.state.ledger.slice(oldLedgerLength);
    if (additions.some(entry => entry.totalCents > 0)) audio.coin();
    else if (additions.some(entry => entry.kind === 'buy' || entry.kind === 'order')) audio.purchase();
    if (additions.some(entry => entry.kind === 'construction')) audio.hammer();
    const activeSteamers = this.state.traffic.filter(visitor => visitor.status !== 'scheduled' && /steamer|showboat/.test(visitor.typeId));
    for (const visitor of activeSteamers) {
      if (!this.knownSteamers.has(visitor.id)) {
        this.knownSteamers.add(visitor.id);
        audio.whistle(TRAFFIC_INTENSITY[visitor.typeId] || 1);
      }
    }
    if (additions.length) this.lastLedgerLength = this.state.ledger.length;
  }

  render(forceLedger) {
    const derived = getDerived(this.state);
    renderHeader(this.root, this.state, derived, this.paused, this.speed, this.muted);
    renderScene(this.root, this.state, derived, this.selection, this.sceneMode, this.interiorPlotIndex);
    renderArrivals(this.root, this.state, derived);
    renderWorkControls(this.root, this.state, derived, this.paused, this.shiftResult);
    const editing = document.activeElement?.matches('input, select, textarea, .ledger-content button, .ledger-content summary, [contenteditable="true"]');
    if (forceLedger || !editing) renderLedger(this.root, this.state, derived, this.activeTab, this.selection, SUPPLY_ROUTES, this.marketFilter, this.shiftResult, this.selectedPlotIndex);
    this.syncPanels();
  }

  renderHeaderOnly() {
    const derived = getDerived(this.state);
    renderHeader(this.root, this.state, derived, this.paused, this.speed, this.muted);
    renderWorkControls(this.root, this.state, derived, this.paused, this.shiftResult);
  }

  persist() {
    try {
      saveGame(this.state);
      this.lastSaveAtMinute = this.state.timeMinutes;
    } catch (error) {
      this.toast(`Save failed: ${error.message}`, 'bad');
    }
  }

  exportSave() {
    try {
      const blob = new Blob([serializeState(this.state)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Trading-Post-Day-${getDerived(this.state).day}.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      this.toast('Save exported.', 'good');
    } catch (error) {
      this.toast(error.message, 'bad');
    }
  }

  async importFile(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      this.state = deserializeState(await file.text());
      this.paused = true;
      this.minuteAccumulator = 0;
      this.shiftResult = null;
      this.sceneMode = 'river';
      this.interiorPlotIndex = null;
      this.selection = null;
      this.selectedPlotIndex = 3;
      this.activeTab = 'desk';
      this.marketFilter = 'shelf';
      this.ledgerViews.clear();
      this.sectionTabs = Object.fromEntries(SECTIONS.map(section => [section.id, section.tabs[0]]));
      this.lastLedgerLength = this.state.ledger.length;
      this.persist();
      this.root.querySelector('[data-modal]')?.remove();
      this.render(true);
      this.toast('Save imported and verified.', 'good');
    } catch (error) {
      this.toast(`Import failed: ${error.message}`, 'bad');
    }
  }

  toast(message, tone = '') {
    const stack = this.root.querySelector('.toast-stack');
    if (!stack) return;
    const node = document.createElement('div');
    node.className = `toast ${tone}`;
    node.textContent = formatCurrencyText(message);
    stack.append(node);
    while (stack.children.length > 4) stack.firstElementChild.remove();
    const timer = setTimeout(() => {
      node.remove();
      this.toastTimers.delete(timer);
    }, 4200);
    this.toastTimers.add(timer);
    return node;
  }
}

const TRAFFIC_INTENSITY = Object.freeze({
  'packet-steamer': 1,
  'luxury-steamer': 1.3,
  'gaming-steamer': 1.5,
  showboat: 1.2,
});

const game = new TradingPostGame(appRoot);
game.boot();

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => navigator.serviceWorker.register('./sw.js').catch(() => {}));
}
